import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Button, Icon, Text, Skeleton } from '../../../../components';
import { CASINO_SETTINGS, COLORS } from '../../../../theme/tokens';
import type { CasinoKind } from '../../../../mock-data/types';
import { SectionSubtitle } from '../shared.styles';
import {
  Grid,
  Choice,
  IconWrap,
  ChoiceTitle,
  ChoiceDesc,
  Check,
  CurrentTag,
  SaveRow,
  SavedHint,
  SkelGrid,
} from './CasinoTypeSection.styles';

export type CasinoTypeSectionProps = {
  value: CasinoKind;
  /** The saved (baseline) type, tagged "Current" once the selection moves off it. */
  saved: CasinoKind;
  /** The selection differs from the saved baseline (enables Save). */
  dirty: boolean;
  /** Briefly true right after a save, for the confirmation state. */
  justSaved: boolean;
  loading: boolean;
  /** Selects a type — the id is read from `event.currentTarget.dataset.id`. */
  onSelect: (event: React.MouseEvent<HTMLElement>) => void;
  onSave: () => void;
};

type ChoiceDef = { id: CasinoKind; title: string; description: string };

/** The currency glyph(s) for a casino type — crypto shows the coin, fiat the wallet, mixed both. */
const ChoiceIcons = ({ id, color }: { id: CasinoKind; color: string }): React.ReactElement => {
  if (id === 'crypto') {
    return <Icon name="crypto" size={20} color={color} />;
  }
  if (id === 'mixed') {
    return (
      <>
        <Icon name="wallet" size={20} color={color} />
        <Icon name="crypto" size={20} color={color} />
      </>
    );
  }
  return <Icon name="wallet" size={20} color={color} />;
};

/**
 * Website Currency — whether players transact in fiat, crypto or both. A deliberate
 * change (pick a card, then Save) mirroring the Settings → Profile dirty/save
 * pattern, so the setting isn't switched by a stray tap. Gates its reveal on
 * `loading`.
 */
export const CasinoTypeSection = ({
  value,
  saved,
  dirty,
  justSaved,
  loading,
  onSelect,
  onSave,
}: CasinoTypeSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const choices: ChoiceDef[] = [
    { id: 'fiat', title: t('Fiat only'), description: t('Players deposit and play with traditional currencies (USD, EUR, …).') },
    { id: 'crypto', title: t('Crypto only'), description: t('Players deposit and play with cryptocurrencies (BTC, ETH, USDT, …).') },
    { id: 'mixed', title: t('Fiat & Crypto'), description: t('Players choose either traditional currencies or crypto when they deposit.') },
  ];
  return (
  <Panel>
    <SectionTitle title={t('Website Currency')} icon="crypto" iconColor={CASINO_SETTINGS.cardIconSelected} />
    <SectionSubtitle text="Choose which currencies players can deposit and play with." tag="span" />

    {loading ? (
      <SkelGrid>
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} block height={132} radius={16} />
        ))}
      </SkelGrid>
    ) : (
      <>
        <Grid>
          {choices.map((choice) => {
            const selected = value === choice.id;
            // Tag the saved type only once the live selection has moved off it.
            // The check already marks the saved card while it's still selected.
            const showCurrentTag = saved === choice.id && !selected;
            return (
              <Choice
                key={choice.id}
                type="button"
                $selected={selected}
                data-id={choice.id}
                aria-pressed={selected}
                onClick={onSelect}
              >
                <IconWrap>
                  <ChoiceIcons id={choice.id} color={selected ? CASINO_SETTINGS.cardIconSelected : CASINO_SETTINGS.cardIcon} />
                </IconWrap>
                <ChoiceTitle text={choice.title} tag="span" />
                <ChoiceDesc text={choice.description} tag="span" />
                {selected ? (
                  <Check>
                    <Icon name="check" size={13} color={COLORS.onAccent} />
                  </Check>
                ) : null}
                {showCurrentTag ? <CurrentTag text="Current" tag="span" /> : null}
              </Choice>
            );
          })}
        </Grid>

        <SaveRow>
          {justSaved ? (
            <SavedHint text="Website currency saved" tag="span" />
          ) : null}
          <Button
            label={justSaved ? t('Saved') : t('Save changes')}
            icon={justSaved ? 'check' : undefined}
            variant="primary"
            disabled={!dirty}
            onClick={onSave}
          />
        </SaveRow>
      </>
    )}
  </Panel>
  );
};

export default CasinoTypeSection;
